import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  DEFAULT_PERSONALIZATION_CONFIG,
  parsePersonalizationConfig,
} from '../../lib/personalization';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { captureEdgeFunctionFailure, reportUiError } from '../../sentry';
import {
  ArrowLeft,
  Loader2,
  Save,
  Trash2,
  Plus,
  Move,
  Calendar,
  Copy,
  Braces,
  Users,
  Send,
  Layers,
  Maximize2,
  Minimize2,
  MoreVertical,
  Pencil,
  X,
} from 'lucide-react';
import { marked } from 'marked';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import PostmarkTemplatePickerModal from '../Automations/PostmarkTemplatePickerModal';
import type { PostmarkTemplate } from '../Automations/types';
import ConfirmSendModal from './ConfirmSendModal';
import ConfirmModal from '../Common/ConfirmModal';
import NewsletterRecipientsTab from './NewsletterRecipientsTab';
import NewsletterRecipientsGate from './NewsletterRecipientsGate';
import NewsletterSendPipelineBanner from './NewsletterSendPipelineBanner';
import NewsletterSettingsTab from './NewsletterSettingsTab';
import NewsletterTrackingTab from './NewsletterTrackingTab';
import NewsletterReportTab from './NewsletterReportTab';
import AddNewsletterContentModal from './AddNewsletterContentModal';
import type { BlockInsertPosition } from './AddNewsletterContentModal';
import {
  channelLabel,
  channelsToKeyArray,
  fetchActiveChannels,
  withCatalogChannels,
  type ChannelRow,
} from '../../lib/channels';
import {
  contentElementsById,
  elementKindLabel,
  fetchAllContentElements,
  filterActiveContentElements,
  newElementSnapshotBlock,
  type ContentElementRow,
} from '../../lib/contentElements';
import {
  blockElementId,
  blockUsesElementReference,
  renderNewsletterBlockToHtml,
} from '../../lib/newsletterBlocks';
import NewsletterContentPreview from './NewsletterContentPreview';
import NewsletterBlocksToolbar, { type BlocksViewMode } from './NewsletterBlocksToolbar';
import {
  DEFAULT_NEWSLETTER_EDIT_TAB,
  NEWSLETTER_BLOCKS_VIEW_QUERY,
  NEWSLETTER_EDIT_TABS,
  newsletterEditPath,
  parseNewsletterBlocksViewMode,
  parseNewsletterEditTab,
  type NewsletterEditTab,
} from '../../lib/newsletterPaths';
import {
  formatScheduledForDisplay,
  getSendPipelineHeaderLine,
  getSendPipelinePhase,
} from '../../lib/newsletterSchedule';
import { canQueueNewsletterRecipients, normalizeNewsletterStatus, newsletterStatusKey, newsletterStatusLabel } from '../../lib/newsletterStatus';

type OutgoingNewsletterRow = Record<string, unknown> & {
  id?: string | number;
  name?: string | null;
  subtitle?: string | null;
  status?: string | null;
  content?: unknown; // JSONB array of blocks
  scheduled_for?: string | null;
  /**
   * Audience targeting. JSONB array of stream keys (matching the keys used in
   * `subscribers.streams`). Empty array = send to all subscribers; otherwise
   * the send pipeline picks subscribers with at least one of these keys set
   * to true on their own `streams` map.
   */
  channels?: unknown;
  /**
   * Which subscriber email_status this send targets: 'confirmed' (default),
   * 'unsubscribed', or 'all'. Channel matching applies in every mode.
   */
  audience_email_status?: string | null;
  /**
   * Subject line shown to subscribers when this newsletter sends. Falls back
   * to `name` when null/blank.
   */
  email_subject?: string | null;
  /**
   * Optional Postmark template alias. When set, the sender cron renders this
   * newsletter via /email/withTemplate and passes the rendered block HTML as
   * a {{{body_html}}} template variable. When null/blank we send raw HTML
   * wrapped in a minimal scaffold.
   */
  postmark_template_alias?: string | null;
  /** Postmark sender signature ID; null = engine default. */
  postmark_signature_id?: number | null;
  personalization_config?: { enabled?: boolean; min_score?: number } | null;
  created_at?: string | null;
  updated_at?: string | null;
};

/**
 * Normalize whatever shape we receive in `streams` (jsonb) into a clean
 * deduped string array of stream keys.
 */
function channelsToArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of value) {
      const s = typeof v === 'string' ? v.trim() : '';
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }
  // Tolerant fallback: if a previous tool wrote `{key: true}`, treat it the
  // same as the array form so we don't lose data.
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return Object.entries(o)
      .filter(([, v]) => v === true)
      .map(([k]) => k.trim())
      .filter(Boolean);
  }
  return [];
}

function labelForChannelSlug(slug: string, catalog: ChannelRow[]): string {
  return channelLabel(slug, catalog);
}

function coerceIdParam(idParam: string | undefined): string | number | null {
  if (!idParam) return null;
  // If it's all digits, treat as number (common for serial ids).
  if (/^\d+$/.test(idParam)) return Number(idParam);
  return idParam;
}

function bestName(row: OutgoingNewsletterRow): string {
  const name = typeof row.name === 'string' ? row.name.trim() : '';
  if (name) return name;
  return '';
}

function statusPillClass(statusRaw: string | null | undefined): string {
  const s = newsletterStatusKey(statusRaw);
  if (s === 'published' || s === 'sent')
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
  if (s === 'ready_to_publish' || s === 'queued')
    return 'bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-200';
  if (s === 'scheduled')
    return 'bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-200';
  if (s === 'on_hold')
    return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200';
  if (s === 'draft') return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
  if (s === 'error') return 'bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-200';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';
}

function blockContentType(b: unknown): 'markdown' | 'html' | 'other' {
  if (!b || typeof b !== 'object' || Array.isArray(b)) return 'markdown';
  const t = String((b as Record<string, unknown>).type ?? 'markdown').toLowerCase();
  if (t === 'html') return 'html';
  if (t === 'markdown') return 'markdown';
  return 'other';
}

function inlineEditorHeight(): number {
  if (typeof window === 'undefined') return 480;
  return Math.min(Math.round(window.innerHeight * 0.52), 520);
}

function fullscreenEditorHeight(): number {
  if (typeof window === 'undefined') return 720;
  // Modal chrome (~5.5rem) + padding; leave a little air at the bottom.
  return Math.max(360, Math.round(window.innerHeight - 120));
}

function InlineMarkdownEditor({
  value,
  onChange,
  onExit,
  onEscape,
  onExpand,
  onCollapse,
  expanded,
  saving,
  blockType,
  blockLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  onExit: (latestValue: string) => void;
  onEscape: () => void;
  onExpand: () => void;
  onCollapse: () => void;
  expanded: boolean;
  saving: boolean;
  blockType: 'markdown' | 'html' | 'other';
  blockLabel?: string;
}) {
  const [editorHeight, setEditorHeight] = useState(() =>
    expanded ? fullscreenEditorHeight() : inlineEditorHeight(),
  );

  useEffect(() => {
    const update = () => {
      setEditorHeight(expanded ? fullscreenEditorHeight() : inlineEditorHeight());
    };
    update();
    if (!expanded) return;
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [expanded]);

  const handleBlurCapture = (e: FocusEvent<HTMLDivElement>) => {
    if (expanded) return;
    const nextTarget = e.relatedTarget as Node | null;
    if (nextTarget && e.currentTarget.contains(nextTarget)) return;
    onExit(value);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    // Fullscreen: only Minimize / Done close — Esc must not dismiss by accident.
    if (expanded) return;
    onEscape();
  };

  const title = blockType === 'html' ? 'HTML editor' : 'Markdown editor';
  const subtitle =
    blockType === 'html'
      ? 'Raw HTML for this block'
      : 'Supports Markdown — **bold**, *italic*, lists, [links](url), ## headings';

  const toolbar = (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-b ${
        blockType === 'html'
          ? 'bg-gray-50 dark:bg-gray-800/80 border-gray-200 dark:border-gray-600'
          : 'bg-gray-50 border-gray-200'
      }`}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!expanded) onExpand();
      }}
      title={expanded ? undefined : 'Double-click for fullscreen'}
    >
      <div className="min-w-0">
        <p
          className={`text-sm font-medium ${
            blockType === 'html' ? 'text-gray-900 dark:text-white' : 'text-gray-900'
          }`}
        >
          {title}
          {blockLabel ? (
            <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">{blockLabel}</span>
          ) : null}
          {expanded ? (
            <span className="ml-2 text-xs font-normal text-emerald-700 dark:text-emerald-300">
              Fullscreen
            </span>
          ) : null}
        </p>
        <p
          className={`text-xs ${
            blockType === 'html' ? 'text-gray-500 dark:text-gray-400' : 'text-gray-500'
          }`}
        >
          {subtitle}
          {!expanded
            ? ' · Double-click header for fullscreen'
            : ' · Use Minimize or Done to close — backdrop and Esc stay open'}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
          {saving ? 'Saving…' : 'Autosaving'}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (expanded) onCollapse();
            else onExpand();
          }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border ${
            blockType === 'html'
              ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700'
              : 'border-gray-300 text-gray-700 hover:bg-white'
          }`}
          title={expanded ? 'Exit fullscreen' : 'Fullscreen editor'}
        >
          {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          {expanded ? 'Minimize' : 'Fullscreen'}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExit(value);
          }}
          className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
            blockType === 'html'
              ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700'
              : 'border-gray-300 text-gray-700 hover:bg-white'
          }`}
        >
          Done
        </button>
      </div>
    </div>
  );

  const editorBody =
    blockType === 'html' ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className={`w-full p-4 text-sm font-mono text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-950 border-0 focus:ring-0 ${
          expanded ? 'flex-1 min-h-0 resize-none' : 'min-h-[min(52vh,520px)] resize-y'
        }`}
        style={expanded ? { height: editorHeight } : undefined}
        spellCheck={false}
      />
    ) : (
      <div data-color-mode="light" className="newsletter-inline-md-editor">
        <MDEditor
          value={value}
          onChange={(v) => onChange(v ?? '')}
          preview="live"
          visibleDragbar={!expanded}
          height={editorHeight}
          textareaProps={{
            placeholder: 'Write Markdown here…',
            onKeyDown: handleKeyDown,
          }}
        />
      </div>
    );

  const shell = (
    <div
      className={
        expanded
          ? `flex flex-col h-full min-h-0 overflow-hidden ${
              blockType === 'html'
                ? 'bg-white dark:bg-gray-900'
                : 'bg-white'
            }`
          : `rounded-lg border shadow-sm overflow-hidden ${
              blockType === 'html'
                ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900'
                : 'newsletter-inline-md-editor border-gray-200 bg-white'
            }`
      }
      onBlurCapture={handleBlurCapture}
      onKeyDown={handleKeyDown}
    >
      {toolbar}
      {editorBody}
    </div>
  );

  if (!expanded) return shell;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col p-3 sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 w-full h-full bg-black/55" aria-hidden />
      <div className="relative z-[81] flex flex-col flex-1 min-h-0 w-full max-w-[1600px] mx-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
        {shell}
      </div>
    </div>,
    document.body,
  );
}

export default function OutgoingNewsletterEditView() {
  const { id, tab: tabParam } = useParams<{ id: string; tab: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const isNew = id === 'new' || !id;
  const coercedId = useMemo(() => (isNew ? null : coerceIdParam(id)), [id, isNew]);
  const autosaveReadyRef = useRef(false);
  const autosaveTimerRef = useRef<number | null>(null);

  const [loading, setLoading] = useState(!isNew);
  const [autosaving, setAutosaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const blocksViewMode = parseNewsletterBlocksViewMode(
    searchParams.get(NEWSLETTER_BLOCKS_VIEW_QUERY) ?? (tabParam === 'preview' ? 'preview' : null),
  );
  const [selectedBlockIdx, setSelectedBlockIdx] = useState<number | null>(null);
  const [rawContentDraft, setRawContentDraft] = useState<string>('[]');
  const [editingBlockIdx, setEditingBlockIdx] = useState<number | null>(null);
  const [editingBlockDraft, setEditingBlockDraft] = useState<string>('');
  const [inlineEditIdx, setInlineEditIdx] = useState<number | null>(null);
  const [inlineEditDraft, setInlineEditDraft] = useState<string>('');
  const [inlineEditSaving, setInlineEditSaving] = useState(false);
  const [inlineEditExpanded, setInlineEditExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [showBoundaries, setShowBoundaries] = useState(true);
  /** Off by default — HTML5 drag fights text selection in the markdown editor. */
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [contentElements, setContentElements] = useState<ContentElementRow[]>([]);
  const [contentElementsLoading, setContentElementsLoading] = useState(true);
  const [addContentOpen, setAddContentOpen] = useState(false);
  const blockListRef = useRef<HTMLDivElement>(null);
  /** Template picker (Settings tab). */
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [removeTemplateConfirmOpen, setRemoveTemplateConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [blockDeleteConfirmIdx, setBlockDeleteConfirmIdx] = useState<number | null>(null);
  const [postmarkTemplates, setPostmarkTemplates] = useState<PostmarkTemplate[]>([]);
  const [postmarkTemplatesLoading, setPostmarkTemplatesLoading] = useState(false);
  const [postmarkTemplatesError, setPostmarkTemplatesError] = useState<string | null>(null);

  /** Confirm-send modal state (Queue recipients / Send now). */
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmSendMode, setConfirmSendMode] = useState<'schedule' | 'send_now'>('schedule');
  const [purging, setPurging] = useState(false);

  /**
   * Whether any scheduled_emails rows exist for this newsletter. Drives
   * whether the Recipients tab is shown for unsent / draft rows.
   */
  const [hasRecipientRows, setHasRecipientRows] = useState(false);
  const [recipientProbeDone, setRecipientProbeDone] = useState(() => id === 'new' || !id);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [audienceChannelCounts, setAudienceChannelCounts] = useState<Record<string, number>>({});

  /** Gear-dropdown state (groups Edit details / Clone / Delete). */
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [newsletterJsonDebugOpen, setNewsletterJsonDebugOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const [actionsMenuPosition, setActionsMenuPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    if (!isActionsOpen || !actionsRef.current) {
      setActionsMenuPosition(null);
      return;
    }
    const syncPosition = () => {
      const rect = actionsRef.current?.getBoundingClientRect();
      if (!rect) return;
      setActionsMenuPosition({
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - 224),
      });
    };
    syncPosition();
    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [isActionsOpen]);

  useEffect(() => {
    if (!isActionsOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (actionsRef.current?.contains(target) || actionsMenuRef.current?.contains(target)) {
        return;
      }
      setIsActionsOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsActionsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isActionsOpen]);

  /**
   * Union of stream keys ever marked `true` on any subscriber. Used to render
   * the Audience picker chips so the user only has to click — they don't have
   * to know the exact key strings.
   */
  const [channelCatalog, setChannelCatalog] = useState<ChannelRow[]>([]);
  const [availableChannels, setAvailableChannels] = useState<string[]>([]);

  const [draft, setDraft] = useState<OutgoingNewsletterRow>({
    name: '',
    subtitle: '',
    status: 'draft',
    content: [],
    scheduled_for: null,
    channels: [],
    email_subject: null,
    postmark_template_alias: null,
    personalization_config: { ...DEFAULT_PERSONALIZATION_CONFIG },
  });

  const newsletterJsonString = useMemo(() => {
    try {
      return JSON.stringify(draft, null, 2);
    } catch {
      return '{}';
    }
  }, [draft]);

  // Tab is the last URL segment (`/outgoing-newsletters/:id/:tab`).
  const requestedTab = parseNewsletterEditTab(tabParam);
  const newsletterStatus = normalizeNewsletterStatus(draft.status as string | null | undefined);
  const showRecipientsTab =
    !isNew &&
    (hasRecipientRows ||
      newsletterStatus === 'ready_to_publish' ||
      newsletterStatus === 'published');
  const showTrackingTab = !isNew;
  const showReportTab = !isNew;
  const editSectionTabs = useMemo(() => {
    const tabs: { id: NewsletterEditTab; label: string }[] = [
      { id: 'settings', label: 'Settings' },
      { id: 'content', label: 'Content' },
    ];
    if (!isNew) tabs.push({ id: 'recipients', label: 'Recipients' });
    if (showTrackingTab) tabs.push({ id: 'tracking', label: 'Tracking' });
    if (showReportTab) tabs.push({ id: 'report', label: 'Report' });
    return tabs;
  }, [isNew, showTrackingTab, showReportTab]);
  const activeTab: NewsletterEditTab =
    requestedTab === 'settings'
      ? 'settings'
      : requestedTab === 'recipients' && !isNew
        ? 'recipients'
        : requestedTab === 'tracking' && showTrackingTab
          ? 'tracking'
          : requestedTab === 'report' && showReportTab
            ? 'report'
            : 'content';
  const newsletterPath = useCallback(
    (tab: NewsletterEditTab, nextId: string | number = id ?? 'new') =>
      newsletterEditPath(nextId, tab, { view: blocksViewMode }),
    [id, blocksViewMode],
  );

  const setActiveTab = useCallback(
    (tab: NewsletterEditTab) => {
      if (!id) return;
      navigate(newsletterPath(tab), { replace: true });
    },
    [id, navigate, newsletterPath],
  );

  const setBlocksViewMode = useCallback(
    (mode: BlocksViewMode) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (mode === 'edit') next.delete(NEWSLETTER_BLOCKS_VIEW_QUERY);
          else next.set(NEWSLETTER_BLOCKS_VIEW_QUERY, mode);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!id || !tabParam) return;
    if (tabParam === 'preview') {
      navigate(newsletterEditPath(id, DEFAULT_NEWSLETTER_EDIT_TAB, { view: 'preview' }), {
        replace: true,
      });
      return;
    }
    if (!NEWSLETTER_EDIT_TABS.includes(tabParam as NewsletterEditTab)) {
      navigate(newsletterPath(DEFAULT_NEWSLETTER_EDIT_TAB), { replace: true });
    }
  }, [id, tabParam, navigate, newsletterPath]);

  useEffect(() => {
    if (!recipientProbeDone || !id) return;
    if (requestedTab === 'recipients' && !isNew && draft.id == null) return;
    if (requestedTab === 'tracking' && !showTrackingTab) {
      navigate(newsletterPath(DEFAULT_NEWSLETTER_EDIT_TAB), { replace: true });
      return;
    }
    if (requestedTab === 'report' && !showReportTab) {
      navigate(newsletterPath(DEFAULT_NEWSLETTER_EDIT_TAB), { replace: true });
    }
  }, [
    recipientProbeDone,
    showTrackingTab,
    showReportTab,
    requestedTab,
    id,
    navigate,
    isNew,
    draft.id,
    newsletterPath,
  ]);

  /** Selected streams for this newsletter, normalized to a string array. */
  const selectedChannels = useMemo(() => channelsToArray(draft.channels), [draft.channels]);

  const elementsById = useMemo(() => contentElementsById(contentElements), [contentElements]);
  const activeContentElements = useMemo(
    () => filterActiveContentElements(contentElements),
    [contentElements],
  );

  const parseMarkdown = useCallback(
    (markdown: string) => marked.parse(markdown, { breaks: true }) as string,
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setContentElementsLoading(true);
      try {
        const rows = await fetchAllContentElements();
        if (!cancelled) setContentElements(rows);
      } catch (e) {
        if (!cancelled) {
          reportUiError('content_elements', 'list', e);
          console.warn(
            'content elements load failed:',
            e instanceof Error ? e.message : e,
          );
          setContentElements([]);
        }
      } finally {
        if (!cancelled) setContentElementsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalog = await fetchActiveChannels();
        const { data, error } = await supabase.from('subscribers').select('channels').limit(5000);
        if (error) throw error;
        const set = new Set<string>();
        for (const row of data ?? []) {
          for (const k of channelsToKeyArray((row as { channels?: unknown }).channels)) {
            set.add(k);
          }
        }
        if (!cancelled) {
          setChannelCatalog(catalog);
          setAvailableChannels(withCatalogChannels(set, catalog));
        }
      } catch (e) {
        if (!cancelled) {
          reportUiError('subscribers', 'list_channels', e);
          console.warn(
            'Failed to load channels from subscribers:',
            e instanceof Error ? e.message : e,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const blocks = useMemo(() => (Array.isArray(draft.content) ? (draft.content as unknown[]) : []), [draft.content]);

  const previewInput = useMemo(
    () => ({
      newsletterId: draft.id ?? null,
      content: blocks,
      name: typeof draft.name === 'string' ? draft.name : null,
      emailSubject: typeof draft.email_subject === 'string' ? draft.email_subject : null,
      postmarkTemplateAlias: (draft.postmark_template_alias as string | null) ?? null,
      subtitle: typeof draft.subtitle === 'string' ? draft.subtitle : null,
    }),
    [blocks, draft.email_subject, draft.id, draft.name, draft.postmark_template_alias, draft.subtitle],
  );

  const handleBlocksViewModeChange = useCallback(
    (mode: BlocksViewMode) => {
      if (blocksViewMode === 'json' && mode !== 'json') {
        try {
          const parsed = JSON.parse(rawContentDraft);
          if (!Array.isArray(parsed)) {
            toast.error('Content must be a JSON array before leaving JSON mode');
            return;
          }
          syncRawFromBlocks(parsed);
        } catch {
          toast.error('Fix JSON syntax before leaving JSON mode');
          return;
        }
      }
      if (mode !== 'edit') {
        setInlineEditIdx(null);
        setInlineEditDraft('');
        setInlineEditExpanded(false);
      }
      setBlocksViewMode(mode);
    },
    [blocksViewMode, rawContentDraft, setBlocksViewMode],
  );

  useEffect(() => {
    if (inlineEditIdx == null) setInlineEditExpanded(false);
  }, [inlineEditIdx]);

  const syncRawFromBlocks = (nextBlocks: unknown[]) => {
    setDraft((d) => ({ ...d, content: nextBlocks }));
    try {
      setRawContentDraft(JSON.stringify(nextBlocks, null, 2));
    } catch {
      setRawContentDraft('[]');
    }
  };

  const scrollToBlockIndex = useCallback((idx: number) => {
    const run = () => {
      const root = blockListRef.current;
      if (!root) return;
      const el = root.querySelector(`[data-block-index="${idx}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(run));
  }, []);

  const insertBlocksAt = useCallback(
    (toInsert: unknown[], position: BlockInsertPosition) => {
      const next =
        position === 'top' ? [...toInsert, ...blocks] : [...blocks, ...toInsert];
      syncRawFromBlocks(next);
      const idx = position === 'top' ? 0 : next.length - toInsert.length;
      setSelectedBlockIdx(idx);
      scrollToBlockIndex(idx);
    },
    [blocks, scrollToBlockIndex],
  );

  const safePretty = (value: unknown) => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const blockTitle = (b: unknown, idx: number): string => {
    if (b && typeof b === 'object' && !Array.isArray(b)) {
      const anyB = b as Record<string, unknown>;
      const t = typeof anyB.type === 'string' ? anyB.type.toLowerCase() : '';
      if (t === 'element') {
        const eid = blockElementId(b);
        const el = eid ? elementsById.get(eid) : undefined;
        return ['element', el?.name ?? eid?.slice(0, 8) ?? `#${idx + 1}`].filter(Boolean).join(' · ');
      }
      const id = typeof anyB.id === 'string' ? anyB.id : '';
      return [t || 'block', id ? id.slice(0, 8) : `#${idx + 1}`].filter(Boolean).join(' · ');
    }
    return `block · #${idx + 1}`;
  };

  const newBlock = (): Record<string, unknown> => ({
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()),
    type: 'markdown',
    content: '',
  });

  const cloneBlock = (b: unknown): Record<string, unknown> => {
    let next: Record<string, unknown> = {};
    if (b && typeof b === 'object' && !Array.isArray(b)) {
      try {
        next = JSON.parse(JSON.stringify(b)) as Record<string, unknown>;
      } catch {
        next = { ...(b as Record<string, unknown>) };
      }
    } else {
      next = { content: String(b ?? '') };
    }
    next.id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());
    if (typeof next.type !== 'string') next.type = 'markdown';
    const t = String(next.type).toLowerCase();
    if (t !== 'element' && typeof next.content !== 'string') next.content = '';
    return next;
  };

  const renderBlock = (b: unknown, idx: number) => {
    if (b && typeof b === 'object' && !Array.isArray(b)) {
      const anyB = b as Record<string, unknown>;
      const type = typeof anyB.type === 'string' ? anyB.type.toLowerCase() : 'markdown';
      const content = typeof anyB.content === 'string' ? anyB.content : '';

      if (type === 'element') {
        const eid = blockElementId(b);
        const el = eid ? elementsById.get(eid) : undefined;
        const html = renderNewsletterBlockToHtml(b, elementsById, parseMarkdown);
        return (
          <div key={idx} className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200 border border-sky-200 dark:border-sky-800">
              <Layers className="w-3 h-3" />
              {el ? `${el.name} · ${elementKindLabel(el.kind)}` : eid ? `Missing element` : 'Element'}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {el
                ? `Legacy linked element (${el.slug}). Use Detach HTML to freeze a copy, or switch to Preview.`
                : 'Element not found — detach or delete this block.'}
            </p>
            {html ? (
              <pre className="mt-2 text-xs whitespace-pre-wrap text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 max-h-40 overflow-auto">
                {html.slice(0, 1200)}
                {html.length > 1200 ? '…' : ''}
              </pre>
            ) : null}
          </div>
        );
      }

      if (type === 'html' || type === 'markdown') {
        const source = content.trim() || safePretty(anyB);
        return (
          <pre
            key={idx}
            className="text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200 bg-gray-50/80 dark:bg-gray-900/40 rounded-lg p-3 border border-gray-100 dark:border-gray-700/80 max-h-[320px] overflow-auto"
          >
            {source}
          </pre>
        );
      }

      return (
        <pre key={idx} className="text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          {safePretty(anyB)}
        </pre>
      );
    }
    return (
      <pre key={idx} className="text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        {safePretty(b)}
      </pre>
    );
  };

  const blockEditableText = (b: unknown): string => {
    if (blockUsesElementReference(b)) {
      const eid = blockElementId(b);
      const el = eid ? elementsById.get(eid) : undefined;
      return el
        ? `[Legacy element link: ${el.name} — use Detach HTML to edit in this newsletter]`
        : `[Legacy element link: ${eid ?? 'unknown'}]`;
    }
    if (!b || typeof b !== 'object' || Array.isArray(b)) return safePretty(b);
    const anyB = b as Record<string, unknown>;
    const content = typeof anyB.content === 'string' ? anyB.content : null;
    return content ?? safePretty(anyB);
  };

  const detachElementBlockAsHtml = (idx: number) => {
    const b = blocks[idx];
    const eid = blockElementId(b);
    const el = eid ? elementsById.get(eid) : undefined;
    if (!el) {
      toast.error('Element not found');
      return;
    }
    const next = [...blocks];
    next[idx] = newElementSnapshotBlock(el);
    syncRawFromBlocks(next);
    toast.success('Converted to HTML block (frozen copy)');
  };

  const applyInlineEdit = (idx: number, raw: string) => {
    const curr = blocks[idx];
    if (!curr || typeof curr !== 'object' || Array.isArray(curr)) {
      const next = [...blocks];
      next[idx] = raw;
      syncRawFromBlocks(next);
      return;
    }
    const anyB = { ...(curr as Record<string, unknown>) };
    anyB.content = raw;
    const next = [...blocks];
    next[idx] = anyB;
    syncRawFromBlocks(next);
  };

  useEffect(() => {
    if (inlineEditIdx == null) return;
    // Debounced autosave for in-situ editing.
    const idx = inlineEditIdx;
    const t = window.setTimeout(() => {
      setInlineEditSaving(true);
      try {
        applyInlineEdit(idx, inlineEditDraft);
      } finally {
        setInlineEditSaving(false);
      }
    }, 450);
    return () => window.clearTimeout(t);
  }, [inlineEditIdx, inlineEditDraft]);

  const flushInlineEdit = (idx: number, value: string) => {
    setInlineEditSaving(true);
    try {
      applyInlineEdit(idx, value);
    } finally {
      setInlineEditSaving(false);
    }
  };

  const moveBlock = (from: number, to: number) => {
    if (from === to) return;
    if (from < 0 || to < 0) return;
    if (from >= blocks.length || to >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    syncRawFromBlocks(next);
    setSelectedBlockIdx(to);
  };

  useEffect(() => {
    if (isNew) return;
    if (coercedId == null) return;

    const load = async () => {
      setLoading(true);
      try {
        const byId = await supabase.from('newsletters').select('*').eq('id', coercedId).maybeSingle();
        if (byId.error) throw byId.error;
        if (!byId.data) {
          toast.error('Newsletter not found');
          navigate('/outgoing-newsletters', { replace: true });
          return;
        }
        const loaded = byId.data as OutgoingNewsletterRow;
        setDraft({
          ...loaded,
          status: normalizeNewsletterStatus(loaded.status as string | null | undefined),
        });
        const initialBlocks = Array.isArray(loaded.content) ? (loaded.content as unknown[]) : [];
        try {
          setRawContentDraft(JSON.stringify(initialBlocks, null, 2));
        } catch {
          setRawContentDraft('[]');
        }
      } catch (e) {
        reportUiError('newsletters', 'load', e, {
          newsletter_id: coercedId != null ? String(coercedId) : 'new',
        });
        toast.error(e instanceof Error ? e.message : 'Failed to load newsletter');
        navigate('/outgoing-newsletters', { replace: true });
      } finally {
        setLoading(false);
        autosaveReadyRef.current = true;
      }
    };

    void load();
  }, [coercedId, isNew, navigate]);

  useEffect(() => {
    if (!isNew) return;
    // Ensure raw editor starts in sync for new drafts.
    try {
      setRawContentDraft(JSON.stringify(Array.isArray(draft.content) ? draft.content : [], null, 2));
    } catch {
      setRawContentDraft('[]');
    }
    autosaveReadyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = isNew ? 'New content' : bestName(draft) || `Newsletter #${String(draft.id ?? id)}`;
  const scheduledLabel = formatScheduledForDisplay(draft.scheduled_for);
  const sendPipelinePhase = useMemo(
    () =>
      getSendPipelinePhase({
        status: newsletterStatus,
        scheduledFor: (draft.scheduled_for as string | null) ?? null,
        hasRecipientRows,
      }),
    [newsletterStatus, draft.scheduled_for, hasRecipientRows],
  );
  const sendPipelineHeaderLine = useMemo(
    () =>
      getSendPipelineHeaderLine({
        phase: sendPipelinePhase,
        status: newsletterStatus,
        scheduledFor: (draft.scheduled_for as string | null) ?? null,
      }),
    [sendPipelinePhase, newsletterStatus, draft.scheduled_for],
  );
  const scheduleChipLabel =
    sendPipelinePhase === 'queued'
      ? `Queued · ${scheduledLabel}`
      : newsletterStatus === 'scheduled' &&
          (sendPipelinePhase === 'planned' || sendPipelinePhase === 'overdue_planned')
        ? `Scheduled · ${scheduledLabel}`
        : sendPipelinePhase === 'planned' ||
            sendPipelinePhase === 'overdue_planned' ||
            sendPipelinePhase === 'draft_planned' ||
            sendPipelinePhase === 'draft_overdue'
          ? `Send date · ${scheduledLabel}`
          : scheduledLabel;
  const postmarkAlias =
    typeof draft.postmark_template_alias === 'string'
      ? draft.postmark_template_alias.trim()
      : '';

  const audienceChipLabel = useMemo(() => {
    let base =
      selectedChannels.length === 0
        ? 'All subscribers'
        : selectedChannels.length === 1
          ? labelForChannelSlug(selectedChannels[0], channelCatalog)
          : `${selectedChannels.length} channels`;
    const emailStatus =
      typeof draft.audience_email_status === 'string'
        ? draft.audience_email_status.trim()
        : '';
    if (emailStatus === 'unsubscribed') base = `${base} · unsubscribed only`;
    else if (emailStatus === 'all') base = `${base} · any email status`;
    if (audienceCount == null) return base;
    return `${base} · ${audienceCount.toLocaleString()} recipient${audienceCount === 1 ? '' : 's'}`;
  }, [selectedChannels, channelCatalog, audienceCount, draft.audience_email_status]);

  const startNameEdit = useCallback(() => {
    const current =
      typeof draft.name === 'string' && draft.name.trim()
        ? draft.name.trim()
        : isNew
          ? ''
          : title;
    setNameDraft(current);
    setEditingName(true);
  }, [draft.name, isNew, title]);

  const commitNameEdit = useCallback(() => {
    const trimmed = nameDraft.trim();
    setDraft((d) => ({ ...d, name: trimmed || null }));
    setEditingName(false);
  }, [nameDraft]);

  const cancelNameEdit = useCallback(() => {
    setEditingName(false);
    setNameDraft('');
  }, []);

  useEffect(() => {
    if (!editingName) return;
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, [editingName]);

  const contentForPersist = (): unknown[] => {
    if (blocksViewMode === 'json') {
      try {
        const parsed = JSON.parse(rawContentDraft);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(draft.content) ? (draft.content as unknown[]) : [];
  };

  const persistDraft = async () => {
    const content = contentForPersist();
    if (blocksViewMode === 'json') {
      try {
        const parsed = JSON.parse(rawContentDraft);
        if (!Array.isArray(parsed)) return;
      } catch {
        return;
      }
    }

    const payload: Record<string, unknown> = {
      name: draft.name ?? null,
      subtitle: draft.subtitle ?? null,
      status: normalizeNewsletterStatus(draft.status as string | null | undefined),
      content,
      scheduled_for: draft.scheduled_for ?? null,
      channels: channelsToArray(draft.channels),
      audience_email_status:
        typeof draft.audience_email_status === 'string' &&
        ['confirmed', 'unsubscribed', 'all'].includes(draft.audience_email_status.trim())
          ? draft.audience_email_status.trim()
          : 'confirmed',
      email_subject: draft.email_subject ?? null,
      postmark_template_alias: draft.postmark_template_alias ?? null,
      postmark_signature_id:
        typeof draft.postmark_signature_id === 'number'
          ? draft.postmark_signature_id
          : null,
      personalization_config: parsePersonalizationConfig(draft.personalization_config),
    };

    // Create on first edit (so refresh doesn't lose changes).
    if (isNew && draft.id == null) {
      const hasAnyContent =
        (typeof payload.name === 'string' && (payload.name as string).trim()) ||
        (typeof payload.subtitle === 'string' && (payload.subtitle as string).trim()) ||
        (typeof payload.scheduled_for === 'string' && (payload.scheduled_for as string).trim()) ||
        (Array.isArray(payload.content) && (payload.content as unknown[]).length > 0);

      if (!hasAnyContent) return;

      if (!user?.id) {
        toast.error('You must be signed in to create content');
        return;
      }
      const ins = await supabase
        .from('newsletters')
        .insert({ ...payload, user_id: user.id })
        .select('*')
        .maybeSingle();
      if (ins.error) throw ins.error;
      if (ins.data) {
        setDraft(ins.data as OutgoingNewsletterRow);
        const newId = (ins.data as any)?.id;
        if (newId != null) {
          navigate(newsletterPath(activeTab, newId), { replace: true });
        }
      }
      return;
    }

    const idToUse = (draft.id ?? coercedId) as any;
    if (idToUse == null) return;
    // NOTE: do NOT `select` and `setDraft(upd.data)` here. The local `draft`
    // is already the source of truth for editing; re-setting it from the
    // response replaces every array/object reference (content, streams, …)
    // which then re-triggers the autosave effect on the next tick and loops
    // forever. The server-computed `updated_at` is not shown in the UI so
    // we don't need it back.
    const upd = await supabase.from('newsletters').update(payload).eq('id', idToUse);
    if (upd.error) throw upd.error;
  };

  useEffect(() => {
    if (!autosaveReadyRef.current) return;
    if (deleting) return;

    if (autosaveTimerRef.current != null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    autosaveTimerRef.current = window.setTimeout(async () => {
      try {
        setAutosaving(true);
        await persistDraft();
      } catch (e) {
        reportUiError('newsletters', 'autosave', e, {
          newsletter_id: String(draft.id ?? coercedId ?? 'new'),
        });
        console.warn(e instanceof Error ? e.message : 'Autosave failed');
      } finally {
        setAutosaving(false);
      }
    }, 900);

    return () => {
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [
    isNew,
    coercedId,
    draft.id,
    draft.name,
    draft.subtitle,
    draft.status,
    draft.scheduled_for,
    draft.content,
    draft.channels,
    draft.audience_email_status,
    draft.email_subject,
    draft.postmark_template_alias,
    draft.personalization_config,
    blocksViewMode,
    rawContentDraft,
    deleting,
  ]);

  const loadPostmarkTemplates = useCallback(async () => {
    setPostmarkTemplatesLoading(true);
    setPostmarkTemplatesError(null);
    try {
      const { data, error } = await supabase.functions.invoke('postmark-templates', {
        body: { activeOnly: true, includeLayouts: true },
      });
      if (error) {
        const detail = (data as { error?: string } | null)?.error;
        throw new Error(detail || error.message);
      }
      const list = (data as { templates?: PostmarkTemplate[] } | null)?.templates ?? [];
      setPostmarkTemplates(list);
    } catch (e) {
      captureEdgeFunctionFailure('postmark-templates', 'list', e);
      setPostmarkTemplatesError(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setPostmarkTemplatesLoading(false);
    }
  }, []);

  const openTemplatePicker = useCallback(() => {
    setTemplatePickerOpen(true);
    if (postmarkTemplates.length === 0 && !postmarkTemplatesLoading) {
      void loadPostmarkTemplates();
    }
  }, [loadPostmarkTemplates, postmarkTemplates.length, postmarkTemplatesLoading]);

  const onDelete = async () => {
    if (isNew) {
      setDeleteConfirmOpen(false);
      navigate('/outgoing-newsletters');
      return;
    }
    if (coercedId == null) return;
    setDeleting(true);
    try {
      const del = await supabase.from('newsletters').delete().eq('id', coercedId);
      if (del.error) throw del.error;
      toast.success('Deleted');
      setDeleteConfirmOpen(false);
      navigate('/outgoing-newsletters', { replace: true });
    } catch (e) {
      reportUiError('newsletters', 'delete', e, { newsletter_id: String(coercedId) });
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Duplicate the current draft as a new `newsletters` row in `draft` status
   * with " (copy)" appended to the name. We deep-clone `content` so edits to
   * the new row don't mutate the original's blocks. Schedule and streams are
   * preserved on the clone — the user can adjust both from the new row.
   */
  const onClone = async () => {
    if (cloning) return;
    if (isNew) {
      toast.error('Save before cloning');
      return;
    }
    if (!user?.id) {
      toast.error('You must be signed in to clone');
      return;
    }
    setCloning(true);
    setIsActionsOpen(false);
    try {
      const currentName = typeof draft.name === 'string' ? draft.name.trim() : '';
      let clonedContent: unknown = [];
      try {
        clonedContent = Array.isArray(draft.content)
          ? JSON.parse(JSON.stringify(draft.content))
          : [];
      } catch {
        clonedContent = Array.isArray(draft.content) ? draft.content : [];
      }
      const payload: Record<string, unknown> = {
        user_id: user.id,
        name: currentName ? `${currentName} (copy)` : '(copy)',
        subtitle: typeof draft.subtitle === 'string' ? draft.subtitle : null,
        status: 'draft',
        content: clonedContent,
        scheduled_for: draft.scheduled_for ?? null,
        channels: channelsToArray(draft.channels),
        email_subject:
          typeof draft.email_subject === 'string' ? draft.email_subject : null,
        postmark_template_alias:
          typeof draft.postmark_template_alias === 'string'
            ? draft.postmark_template_alias
            : null,
      };
      const ins = await supabase
        .from('newsletters')
        .insert(payload)
        .select('id')
        .maybeSingle();
      if (ins.error) throw ins.error;
      const newId = (ins.data as { id?: string | number } | null)?.id;
      if (newId == null) throw new Error('Insert did not return an id');
      toast.success('Cloned');
      navigate(newsletterEditPath(newId, DEFAULT_NEWSLETTER_EDIT_TAB));
    } catch (e) {
      reportUiError('newsletters', 'clone', e, {
        newsletter_id: draft.id != null ? String(draft.id) : undefined,
      });
      toast.error(e instanceof Error ? e.message : 'Clone failed');
    } finally {
      setCloning(false);
    }
  };

  /**
   * Cheap "are there any rows?" probe used to decide whether to show the
   * Recipients tab when the newsletter is still in Draft (e.g. user purged
   * but rows might still exist with non-pending statuses worth viewing).
   */
  useEffect(() => {
    if (draft.id == null) {
      setHasRecipientRows(false);
      // Only mark probe complete when there is no newsletter to load (new draft).
      // Otherwise keep recipientProbeDone false until draft.id is set and counted.
      if (isNew) {
        setRecipientProbeDone(true);
      }
      return;
    }
    let cancelled = false;
    setRecipientProbeDone(false);
    (async () => {
      try {
        const { count, error } = await supabase
          .from('scheduled_emails')
          .select('id', { count: 'exact', head: true })
          .eq('newsletter_id', draft.id);
        if (error) throw error;
        if (!cancelled) {
          setHasRecipientRows((count ?? 0) > 0);
          setRecipientProbeDone(true);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn(
            'hasRecipientRows probe failed:',
            e instanceof Error ? e.message : e,
          );
          setRecipientProbeDone(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft.id, draft.status, isNew]);

  /**
   * Live audience preview from the local draft (channels + email status), so
   * the header badge and settings panel update immediately — before autosave
   * lands. Also returns a per-channel breakdown for the channel chips.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const emailStatus =
          typeof draft.audience_email_status === 'string' &&
          ['confirmed', 'unsubscribed', 'all'].includes(draft.audience_email_status.trim())
            ? draft.audience_email_status.trim()
            : 'confirmed';
        const { data, error } = await supabase.rpc('newsletter_audience_preview', {
          p_channels: channelsToKeyArray(draft.channels),
          p_audience_email_status: emailStatus,
        });
        if (cancelled) return;
        if (error) {
          console.warn('audiencePreview:', error.message);
          setAudienceCount(null);
          setAudienceChannelCounts({});
          return;
        }
        const preview = (data ?? {}) as { total?: number; channels?: Record<string, number> };
        setAudienceCount(typeof preview.total === 'number' ? preview.total : 0);
        setAudienceChannelCounts(
          preview.channels && typeof preview.channels === 'object' ? preview.channels : {},
        );
      } catch {
        if (!cancelled) {
          setAudienceCount(null);
          setAudienceChannelCounts({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft.channels, draft.audience_email_status]);

  /**
   * Reload `draft` from the DB. Used after the confirm-send modal flips status
   * to `Ready to Publish` and after Purge resets it to `Draft`, so the local
   * UI immediately reflects the new state without a full page refresh.
   */
  const reloadDraftFromDb = async () => {
    if (draft.id == null) return;
    try {
      const { data, error } = await supabase
        .from('newsletters')
        .select('*')
        .eq('id', draft.id)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setDraft({
          ...(data as OutgoingNewsletterRow),
          status: normalizeNewsletterStatus((data as OutgoingNewsletterRow).status as string | null | undefined),
        });
      }
    } catch (e) {
      reportUiError('newsletters', 'reload', e, {
        newsletter_id: draft.id != null ? String(draft.id) : undefined,
      });
      console.warn('reloadDraftFromDb:', e instanceof Error ? e.message : e);
    }
  };

  const openConfirmSend = (mode: 'schedule' | 'send_now') => {
    if (draft.id == null) {
      toast.error('Save the newsletter before sending');
      return;
    }
    if (mode === 'schedule' && !draft.scheduled_for) {
      toast.error('Pick a planned send date and time first (Settings)');
      return;
    }
    if (!Array.isArray(draft.content) || (draft.content as unknown[]).length === 0) {
      toast.error('Add some content blocks before sending');
      return;
    }
    setConfirmSendMode(mode);
    setConfirmSendOpen(true);
    setIsActionsOpen(false);
    if (postmarkTemplates.length === 0 && !postmarkTemplatesLoading) {
      void loadPostmarkTemplates();
    }
  };

  const onPurgeScheduled = async () => {
    if (draft.id == null) return;
    if (
      !window.confirm(
        'Delete every pending scheduled send for this newsletter and revert to Draft?',
      )
    ) {
      return;
    }
    setPurging(true);
    setIsActionsOpen(false);
    try {
      const { data, error } = await supabase.rpc('purge_newsletter_pending_sends', {
        p_newsletter_id: draft.id,
      });
      if (error) throw error;
      const removed = typeof data === 'number' ? data : 0;
      toast.success(
        removed > 0 ? `Purged ${removed} pending send(s)` : 'Nothing pending to purge',
      );
      await reloadDraftFromDb();
    } catch (e) {
      reportUiError('newsletters', 'purge_pending_sends', e, {
        newsletter_id: String(draft.id),
      });
      toast.error(e instanceof Error ? e.message : 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="p-10 flex justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-emerald-500 rounded-full border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <header className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-4 sm:px-5 pt-3 pb-1">
          <button
            type="button"
            onClick={() => navigate('/outgoing-newsletters')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            <ArrowLeft className="w-4 h-4" />
            All newsletters
          </button>
        </div>

        <div className="px-4 sm:px-5 pb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              {editingName ? (
                <input
                  ref={nameInputRef}
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitNameEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitNameEdit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelNameEdit();
                    }
                  }}
                  className="flex-1 min-w-0 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-snug bg-transparent border-b-2 border-emerald-500 focus:outline-none focus:border-emerald-600 py-0.5"
                  placeholder="Newsletter name"
                  aria-label="Newsletter name"
                />
              ) : (
                <>
                  <h1
                    className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-snug truncate"
                    title={title}
                  >
                    {title}
                  </h1>
                  <button
                    type="button"
                    onClick={startNameEdit}
                    className="shrink-0 inline-flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                    title="Edit name"
                    aria-label="Edit name"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold ${statusPillClass(
                draft.status,
              )}`}
            >
              {newsletterStatusLabel(draft.status as string | null | undefined)}
            </span>
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={
                sendPipelinePhase === 'draft_planned' || sendPipelinePhase === 'draft_overdue'
                  ? 'Draft with a send date — planning only until status is Scheduled'
                  : sendPipelinePhase === 'planned' || sendPipelinePhase === 'overdue_planned'
                    ? 'Scheduled — committed to this send date; recipients not queued yet'
                    : sendPipelinePhase === 'queued'
                      ? 'Recipients queued for this send time'
                      : 'Change planned send date in Settings'
              }
            >
              <Calendar className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              {scheduleChipLabel}
            </button>
            {sendPipelinePhase === 'planned' ||
            sendPipelinePhase === 'overdue_planned' ||
            sendPipelinePhase === 'draft_planned' ||
            sendPipelinePhase === 'draft_overdue' ? (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border ${
                  sendPipelinePhase === 'draft_planned' || sendPipelinePhase === 'draft_overdue'
                    ? 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200'
                    : sendPipelinePhase === 'overdue_planned'
                      ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200'
                      : 'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200'
                }`}
              >
                {sendPipelinePhase === 'draft_planned' || sendPipelinePhase === 'draft_overdue'
                  ? 'Not committed'
                  : 'Not queued'}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={
                selectedChannels.length === 0
                  ? audienceCount != null
                    ? `Sends to all subscribers (${audienceCount} match now). Click to change audience.`
                    : 'Sends to all subscribers. Click to change audience.'
                  : `Audience: ${selectedChannels
                      .map((s) => labelForChannelSlug(s, channelCatalog))
                      .join(', ')}${
                      audienceCount != null ? ` · ${audienceCount} subscribers match` : ''
                    }. Click to change.`
              }
            >
              <Users className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              {audienceChipLabel}
            </button>
              {postmarkAlias ? (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 font-mono"
                  title="Postmark template"
                >
                  <Layers className="w-3.5 h-3.5 shrink-0" />
                  {postmarkAlias}
                </span>
              ) : null}
            </div>
            {sendPipelineHeaderLine ? (
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-relaxed max-w-3xl">
                {sendPipelineHeaderLine}
              </p>
            ) : null}

          </div>

          <div className="flex items-center gap-2 shrink-0 self-start">
          {autosaving ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />
              Saving…
            </span>
          ) : null}
          <div className="relative" ref={actionsRef}>
            <button
              type="button"
              onClick={() => setIsActionsOpen((v) => !v)}
              className={`inline-flex items-center justify-center p-2 rounded-lg border transition-colors ${
                isActionsOpen
                  ? 'border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
              title="Send, clone, delete, and more"
              aria-label="Actions"
              aria-haspopup="menu"
              aria-expanded={isActionsOpen}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {isActionsOpen && actionsMenuPosition
              ? createPortal(
                  <div
                    ref={actionsMenuRef}
                    role="menu"
                    style={{ top: actionsMenuPosition.top, left: actionsMenuPosition.left }}
                    className="fixed w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-1 z-[200]"
                  >
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => {
                    setIsActionsOpen(false);
                    setNewsletterJsonDebugOpen(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <Braces className="w-4 h-4" />
                  JSON
                </button>
                {!isNew && canQueueNewsletterRecipients(newsletterStatus) ? (
                  <>
                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => openConfirmSend('schedule')}
                      disabled={!draft.scheduled_for}
                      title={!draft.scheduled_for ? 'Set a planned send date in Settings first' : undefined}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
                    >
                      <Calendar className="w-4 h-4" />
                      Queue recipients…
                    </button>
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => openConfirmSend('send_now')}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <Send className="w-4 h-4" />
                      Send now…
                    </button>
                  </>
                ) : null}
                {!isNew && newsletterStatus === 'ready_to_publish' ? (
                  <>
                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => void onPurgeScheduled()}
                      disabled={purging}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      {purging ? 'Purging…' : 'Purge scheduled'}
                    </button>
                  </>
                ) : null}
                {!isNew ? (
                  <>
                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => void onClone()}
                      disabled={cloning}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
                    >
                      <Copy className="w-4 h-4" />
                      {cloning ? 'Cloning…' : 'Clone'}
                    </button>
                  </>
                ) : null}
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => {
                    setIsActionsOpen(false);
                    setDeleteConfirmOpen(true);
                  }}
                  disabled={deleting}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {isNew ? 'Discard' : deleting ? 'Deleting…' : 'Delete'}
                </button>
                  </div>,
                  document.body,
                )
              : null}
          </div>
        </div>
        </div>

        {editSectionTabs.length > 1 ? (
          <div
            className="flex gap-1 px-4 sm:px-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40"
            role="tablist"
            aria-label="Newsletter sections"
          >
            {editSectionTabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                type="button"
                title={
                  tab.id === 'recipients' && !showRecipientsTab
                    ? 'Queue recipients via Actions to unlock the recipient list'
                    : undefined
                }
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300 bg-white dark:bg-gray-800'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      {!isNew && (recipientProbeDone || sendPipelinePhase === 'on_hold') ? (
        <NewsletterSendPipelineBanner
          phase={sendPipelinePhase}
          status={newsletterStatus}
          scheduledFor={(draft.scheduled_for as string | null) ?? null}
          onQueueRecipients={() => openConfirmSend('schedule')}
          queueDisabled={!draft.scheduled_for}
          queueDisabledReason="Set a planned send date in Settings first"
        />
      ) : null}

      {activeTab === 'settings' ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
          <NewsletterSettingsTab
            draft={draft}
            setDraft={setDraft}
            channelCatalog={channelCatalog}
            availableChannels={availableChannels}
            audienceCount={audienceCount}
            channelCounts={audienceChannelCounts}
            postmarkTemplates={postmarkTemplates}
            postmarkTemplatesLoading={postmarkTemplatesLoading}
            postmarkTemplatesError={postmarkTemplatesError}
            onLoadPostmarkTemplates={loadPostmarkTemplates}
            onOpenTemplatePicker={openTemplatePicker}
            onRequestRemoveTemplate={() => setRemoveTemplateConfirmOpen(true)}
          />
        </div>
      ) : activeTab === 'recipients' && draft.id == null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-10 flex justify-center">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : activeTab === 'recipients' && draft.id != null && !recipientProbeDone ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-10 flex justify-center">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : activeTab === 'recipients' && draft.id != null && !showRecipientsTab ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
          <NewsletterRecipientsGate
            hasInboxSubject={Boolean(
              ((draft.email_subject as string | null) ?? '').trim() ||
                ((draft.name as string | null) ?? '').trim(),
            )}
            hasPostmarkTemplate={Boolean(
              ((draft.postmark_template_alias as string | null) ?? '').trim(),
            )}
            hasContentBlocks={blocks.length > 0}
            hasSchedule={Boolean((draft.scheduled_for as string | null) ?? '')}
            audienceLabel={
              selectedChannels.length > 0
                ? selectedChannels.join(', ')
                : 'All subscribers (no channel filter)'
            }
            onOpenSettings={() => setActiveTab('settings')}
            onOpenContent={() => setActiveTab('content')}
            onOpenActions={() => setIsActionsOpen(true)}
          />
        </div>
      ) : activeTab === 'recipients' && draft.id != null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
          <NewsletterRecipientsTab
            newsletterId={String(draft.id)}
            newsletterName={bestName(draft) || 'Untitled'}
            canPurge={newsletterStatus === 'ready_to_publish'}
            onPurged={() => void reloadDraftFromDb()}
          />
        </div>
      ) : activeTab === 'tracking' && draft.id != null ? (
        <NewsletterTrackingTab newsletterId={String(draft.id)} />
      ) : activeTab === 'report' && draft.id != null ? (
        <NewsletterReportTab
          newsletterId={String(draft.id)}
          scheduledFor={(draft.scheduled_for as string | null) ?? null}
        />
      ) : activeTab === 'content' ? (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6 space-y-6">
        <NewsletterBlocksToolbar
          blocksCount={blocks.length}
          blocksViewMode={blocksViewMode}
          onBlocksViewModeChange={handleBlocksViewModeChange}
          showBoundaries={showBoundaries}
          onShowBoundariesChange={setShowBoundaries}
          reorderEnabled={reorderEnabled}
          onReorderEnabledChange={(enabled) => {
            setReorderEnabled(enabled);
            if (!enabled) setDragIdx(null);
          }}
          onAddContent={() => setAddContentOpen(true)}
        />

        {blocksViewMode === 'json' ? (
          <div>
            <textarea
              value={rawContentDraft}
              onChange={(e) => setRawContentDraft(e.target.value)}
              className="w-full min-h-[420px] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="[]"
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Must be a JSON array of content blocks.
              </p>
              <button
                type="button"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(rawContentDraft);
                    if (!Array.isArray(parsed)) {
                      toast.error('Content must be a JSON array');
                      return;
                    }
                    syncRawFromBlocks(parsed);
                    toast.success('JSON applied');
                    setBlocksViewMode('edit');
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : 'Invalid JSON');
                  }
                }}
                className="text-xs font-medium text-emerald-600 hover:underline dark:text-emerald-400"
              >
                Open block editor
              </button>
            </div>
          </div>
        ) : blocksViewMode === 'preview' ? (
          <NewsletterContentPreview previewInput={previewInput} />
        ) : (
          <div className="space-y-3">
            {blocks.length === 0 ? (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 p-6 text-sm text-gray-500 dark:text-gray-400">
                No blocks yet. Use <span className="font-medium">Add content</span> to add a block or
                insert from the element library.
              </div>
            ) : (
              <div ref={blockListRef} className="space-y-3">
                {blocks.map((b, idx) => {
                  const isSelected = selectedBlockIdx === idx;
                  const isInlineEditing = inlineEditIdx === idx;
                  const contentType = blockContentType(b);
                  const canDrag = reorderEnabled && !isInlineEditing;
                  return (
                    <div
                      key={idx}
                      data-block-index={idx}
                      draggable={canDrag}
                      onDragStart={(e) => {
                        if (!canDrag) {
                          e.preventDefault();
                          return;
                        }
                        setDragIdx(idx);
                      }}
                      onDragEnd={() => setDragIdx(null)}
                      onDragOver={(e) => {
                        if (!reorderEnabled) return;
                        e.preventDefault();
                      }}
                      onDrop={() => {
                        if (!reorderEnabled || dragIdx == null) return;
                        moveBlock(dragIdx, idx);
                        setDragIdx(null);
                      }}
                      onClick={() => {
                        setSelectedBlockIdx(idx);
                      }}
                      onDoubleClick={() => {
                        setSelectedBlockIdx(idx);
                        setInlineEditDraft(blockEditableText(b));
                        setInlineEditIdx(idx);
                        setInlineEditExpanded(true);
                      }}
                      className={[
                        'group relative rounded-xl bg-white dark:bg-gray-800',
                        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
                        showBoundaries ? 'border border-gray-200 dark:border-gray-700' : 'border border-transparent',
                        'hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors',
                        isInlineEditing
                          ? 'ring-2 ring-emerald-500/50 dark:ring-emerald-500/40 border-emerald-300 dark:border-emerald-700'
                          : isSelected
                            ? 'ring-2 ring-emerald-400/40 dark:ring-emerald-600/40'
                            : '',
                      ].join(' ')}
                      title={
                        isInlineEditing
                          ? 'Editing — click Done or click outside to finish'
                          : canDrag
                            ? 'Drag to reorder. Turn off Reorder in the toolbar to select text while editing.'
                            : 'Click Edit for in-place editing, or double-click for fullscreen. Turn on Reorder to drag blocks.'
                      }
                    >
                      <span className="sr-only">{blockTitle(b, idx)}</span>
                      <div
                        className={
                          showBoundaries
                            ? 'flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700/80'
                            : 'absolute right-3 top-3 z-10 flex items-center gap-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity'
                        }
                      >
                        {showBoundaries ? (
                          <div className="min-w-0 flex-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                            {blockTitle(b, idx)}
                          </div>
                        ) : null}
                        <div className="shrink-0 flex items-center gap-2">
                          {!blockUsesElementReference(b) ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (inlineEditIdx === idx) {
                                  flushInlineEdit(idx, inlineEditDraft);
                                  setInlineEditExpanded(false);
                                  setInlineEditIdx(null);
                                  setInlineEditDraft('');
                                  return;
                                }
                                setSelectedBlockIdx(idx);
                                setInlineEditExpanded(false);
                                setInlineEditIdx(idx);
                                setInlineEditDraft(blockEditableText(b));
                              }}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-medium ${
                                inlineEditIdx === idx
                                  ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                                  : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                              }`}
                              title={
                                inlineEditIdx === idx
                                  ? 'Finish editing'
                                  : contentType === 'html'
                                    ? 'Edit HTML'
                                    : 'Edit Markdown'
                              }
                            >
                              <Pencil className="w-4 h-4" />
                              {inlineEditIdx === idx ? 'Done' : 'Edit'}
                            </button>
                          ) : null}
                          {canDrag ? (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300"
                              title="Drag this block to reorder"
                            >
                              <Move className="w-4 h-4" />
                              Drag
                            </span>
                          ) : null}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = [...blocks];
                              next.splice(idx + 1, 0, newBlock());
                              syncRawFromBlocks(next);
                              const newIdx = idx + 1;
                              setSelectedBlockIdx(newIdx);
                              scrollToBlockIndex(newIdx);
                            }}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                            title="Insert block below"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = [...blocks];
                              next.splice(idx + 1, 0, cloneBlock(b));
                              syncRawFromBlocks(next);
                              setSelectedBlockIdx(idx + 1);
                            }}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                            title="Duplicate block"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBlockDeleteConfirmIdx(idx);
                            }}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                            title="Delete block"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {blockUsesElementReference(b) ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                detachElementBlockAsHtml(idx);
                              }}
                              className="inline-flex items-center justify-center px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/40 text-xs font-medium"
                              title="Copy element HTML into this block as editable HTML"
                            >
                              Detach HTML
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingBlockIdx(idx);
                              setEditingBlockDraft(safePretty(b));
                            }}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                            title="Edit block JSON"
                          >
                            <Braces className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className={`px-4 pb-4 ${showBoundaries ? '' : 'pt-4'}`}>
                        {inlineEditIdx === idx && !blockUsesElementReference(b) ? (
                          <div className="space-y-2">
                            {inlineEditExpanded ? (
                              <div className="rounded-lg border border-dashed border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/20 px-4 py-6 text-center text-sm text-emerald-800 dark:text-emerald-200">
                                Editing in fullscreen — use Minimize or Done to return here.
                              </div>
                            ) : null}
                            <InlineMarkdownEditor
                              value={inlineEditDraft}
                              blockType={contentType}
                              blockLabel={`Block #${idx + 1}`}
                              expanded={inlineEditExpanded}
                              saving={inlineEditSaving}
                              onChange={setInlineEditDraft}
                              onExpand={() => setInlineEditExpanded(true)}
                              onCollapse={() => setInlineEditExpanded(false)}
                              onEscape={() => {
                                setInlineEditExpanded(false);
                                setInlineEditIdx(null);
                                setInlineEditDraft('');
                              }}
                              onExit={(latestValue) => {
                                flushInlineEdit(idx, latestValue);
                                setInlineEditExpanded(false);
                                setInlineEditIdx(null);
                                setInlineEditDraft('');
                              }}
                            />
                          </div>
                        ) : (
                          renderBlock(b, idx)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      ) : null}

      {editingBlockIdx != null ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 w-full h-full bg-black/50"
            aria-label="Close"
            onClick={() => {
              setEditingBlockIdx(null);
              setEditingBlockDraft('');
            }}
          />
          <div className="relative z-[71] w-full max-w-4xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                Edit block #{editingBlockIdx + 1}
              </div>
              <button
                type="button"
                className="text-sm text-gray-600 dark:text-gray-300 hover:underline"
                onClick={() => {
                  setEditingBlockIdx(null);
                  setEditingBlockDraft('');
                }}
              >
                Close
              </button>
            </div>
            <div className="p-5">
              <textarea
                value={editingBlockDraft}
                onChange={(e) => setEditingBlockDraft(e.target.value)}
                className="w-full min-h-[420px] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Block must be valid JSON (object recommended). You can add metadata under a <span className="font-mono">meta</span> key.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const parsed = JSON.parse(editingBlockDraft);
                      const idx = editingBlockIdx;
                      const next = [...blocks];
                      next[idx] = parsed;
                      syncRawFromBlocks(next);
                      toast.success('Block updated');
                      setEditingBlockIdx(null);
                      setEditingBlockDraft('');
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : 'Invalid JSON');
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  <Save className="w-4 h-4" />
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AddNewsletterContentModal
        isOpen={addContentOpen}
        onClose={() => setAddContentOpen(false)}
        elements={activeContentElements}
        loading={contentElementsLoading}
        onConfirm={(result) => {
          const position: BlockInsertPosition = result.position;
          if (result.type === 'blank') {
            insertBlocksAt([newBlock()], position);
            toast.success(`Added blank block at ${position}`);
          } else {
            insertBlocksAt([newElementSnapshotBlock(result.element)], position);
            toast.success(`Inserted element: ${result.element.name}`);
          }
        }}
      />

      <PostmarkTemplatePickerModal
        isOpen={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        templates={postmarkTemplates}
        loading={postmarkTemplatesLoading}
        error={postmarkTemplatesError}
        onRefresh={() => void loadPostmarkTemplates()}
        currentValue={((draft.postmark_template_alias as string | null) ?? '').trim() || null}
        onSelect={(t) => {
          const value = t.alias ?? String(t.templateId);
          setDraft((d) => ({ ...d, postmark_template_alias: value }));
        }}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        title={isNew ? 'Discard draft?' : 'Delete content?'}
        description={
          isNew ? (
            'Leave this page and discard unsaved changes.'
          ) : (
            <>
              <p>
                This permanently deletes{' '}
                <span className="font-mono text-gray-900 dark:text-gray-100">
                  {(typeof draft.name === 'string' && draft.name.trim()) || 'this content item'}
                </span>{' '}
                and all of its blocks.
              </p>
              <p className="mt-2">This cannot be undone.</p>
            </>
          )
        }
        confirmLabel={isNew ? 'Discard' : 'Delete'}
        variant="danger"
        busy={deleting}
        onConfirm={() => void onDelete()}
        onClose={() => {
          if (!deleting) setDeleteConfirmOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={blockDeleteConfirmIdx != null}
        title="Delete block?"
        description={
          blockDeleteConfirmIdx != null ? (
            <>
              <p>
                Remove block #{blockDeleteConfirmIdx + 1}
                {blocks[blockDeleteConfirmIdx]
                  ? ` (${blockTitle(blocks[blockDeleteConfirmIdx], blockDeleteConfirmIdx)})`
                  : ''}{' '}
                from this content item.
              </p>
              <p className="mt-2">This cannot be undone.</p>
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (blockDeleteConfirmIdx == null) return;
          const idx = blockDeleteConfirmIdx;
          const next = blocks.filter((_, i) => i !== idx);
          syncRawFromBlocks(next);
          setSelectedBlockIdx(next.length ? Math.min(idx, next.length - 1) : null);
          if (inlineEditIdx === idx) {
            setInlineEditExpanded(false);
            setInlineEditIdx(null);
            setInlineEditDraft('');
          }
          setBlockDeleteConfirmIdx(null);
        }}
        onClose={() => setBlockDeleteConfirmIdx(null)}
      />

      <ConfirmModal
        isOpen={removeTemplateConfirmOpen}
        title="Remove template?"
        description="This newsletter will send as raw HTML (no Postmark template wrapper) until you pick another one."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => {
          setDraft((d) => ({ ...d, postmark_template_alias: null }));
          setRemoveTemplateConfirmOpen(false);
        }}
        onClose={() => setRemoveTemplateConfirmOpen(false)}
      />

      {draft.id != null ? (
        <ConfirmSendModal
          isOpen={confirmSendOpen}
          mode={confirmSendMode}
          newsletterId={String(draft.id)}
          scheduledFor={(draft.scheduled_for as string | null) ?? null}
          emailSubject={(draft.email_subject as string | null) ?? null}
          name={(draft.name as string | null) ?? null}
          channels={selectedChannels}
          postmarkTemplateAlias={(draft.postmark_template_alias as string | null) ?? null}
          aiPersonalizationEnabled={
            parsePersonalizationConfig(draft.personalization_config).enabled
          }
          templateLabel={(() => {
            const alias = ((draft.postmark_template_alias as string | null) ?? '').trim();
            if (!alias) return null;
            const tpl = postmarkTemplates.find(
              (t) => String(t.templateId) === alias || t.alias === alias,
            );
            return tpl?.name ?? alias;
          })()}
          onClose={() => setConfirmSendOpen(false)}
          onSuccess={async ({ inserted, recipients_total }) => {
            setConfirmSendOpen(false);
            toast.success(
              inserted > 0
                ? `Scheduled ${inserted} of ${recipients_total} recipient${
                    recipients_total === 1 ? '' : 's'
                  }`
                : 'No new recipients to schedule (already materialized)',
            );
            await reloadDraftFromDb();
          }}
        />
      ) : null}

      {newsletterJsonDebugOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="newsletter-json-debug-title"
          onClick={() => setNewsletterJsonDebugOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[min(85vh,640px)] flex flex-col border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h3
                id="newsletter-json-debug-title"
                className="text-sm font-semibold text-gray-900 dark:text-white truncate pr-2"
              >
                Newsletter JSON (debug)
                {typeof draft.name === 'string' && draft.name.trim() ? (
                  <span className="font-normal text-gray-500 dark:text-gray-400">
                    {' '}
                    · {draft.name.trim()}
                  </span>
                ) : null}
              </h3>
              <button
                type="button"
                onClick={() => setNewsletterJsonDebugOpen(false)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                aria-label="Close debug panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
              {newsletterJsonString}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}

